// src/Frameworks/billing/BillingClient.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Elements kept available but default is Hosted Checkout for reliability in dev.
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

const API = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

function authHeaders() {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    "";
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

async function apiGet(path) {
  const r = await fetch(path, { headers: authHeaders(), credentials: "include" });
  if (!r.ok) throw new Error(`${path} failed: ${r.status}`);
  return r.json();
}
async function apiPost(path, body) {
  const r = await fetch(path, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    let msg = await r.text();
    try { msg = JSON.parse(msg).detail || msg; } catch {}
    const e = new Error(msg); e.status = r.status;
    throw e;
  }
  return r.json();
}

const STATUS_POLL_MS = 1200;
const STATUS_POLL_MAX_TRIES = 20;

const Ctx = createContext(null);

export function BillingProvider({ children, onWarning, onExpired }) {
  const [status, setStatus] = useState({
    credits: 0,
    active_window: null,
    now_utc: null,
    seconds_remaining: null,
    has_saved_card: null,
    subscription: null,
  });
  const [loading, setLoading] = useState(true);

  // Optional in-app Payment Element modal (unused by default)
  const [modalOpen, setModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  // ----- countdown state/refs -----
  const [secondsRemaining, setSecondsRemaining] = useState(null); // <- drives re-render each second
  const serverNowRef = useRef(null);    // ms baseline of server "now_utc" when captured
  const clientStartRef = useRef(null);  // ms local time when we captured server now
  const expiresAtRef = useRef(null);    // ms expires_at
  const tickRef = useRef(null);
  const syncRef = useRef(null);
  const warned5 = useRef(false);
  const warned1 = useRef(false);

  const stopTicker = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  }, []);

  function startTickingFromStatus(s) {
    const expiresISO = s?.active_session?.expires_at || s?.active_window?.expires_at || null;
    const nowISO = s?.now_utc || null;

    // No active session → stop ticking
    if (!expiresISO || !nowISO) {
      expiresAtRef.current = null;
      serverNowRef.current = null;
      clientStartRef.current = null;
      warned5.current = false;
      warned1.current = false;
      stopTicker();
      setSecondsRemaining(null);
      return;
    }

    // Store baselines
    expiresAtRef.current = Date.parse(expiresISO);
    serverNowRef.current = Date.parse(nowISO);
    clientStartRef.current = Date.now();
    warned5.current = false;
    warned1.current = false;

    // Prime value
    {
      const s0 = Math.max(
        0,
        Math.floor((expiresAtRef.current - serverNowRef.current) / 1000)
      );
      setSecondsRemaining(s0);
    }

    // Restart 1s ticker (server-anchored; no drift)
    stopTicker();
    tickRef.current = setInterval(() => {
      if (!expiresAtRef.current || !serverNowRef.current || !clientStartRef.current) {
        setSecondsRemaining(null);
        return;
      }
      const elapsedLocal = Date.now() - clientStartRef.current;
      const estServerNow = serverNowRef.current + elapsedLocal;
      const sLeft = Math.max(0, Math.floor((expiresAtRef.current - estServerNow) / 1000));
      setSecondsRemaining(sLeft);

      if (sLeft === 300 && !warned5.current) {
        warned5.current = true;
        try { onWarning?.("5 minutes left"); } catch {}
      }
      if (sLeft === 60 && !warned1.current) {
        warned1.current = true;
        try { onWarning?.("1 minute left"); } catch {}
      }
      if (sLeft === 0) {
        stopTicker();
        try { onExpired?.(); } catch {}
      }
    }, 1000);
  }

  const refreshStatus = useCallback(async () => {
    const s = await apiGet(`${API}/billing/status`);
    setStatus(s);
    setLoading(false);
    startTickingFromStatus(s); // anchor ticker to latest server times
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial + periodic sync
  useEffect(() => {
    refreshStatus().catch(console.error);
    // light periodic clock re-sync (and to pick up server-side changes)
    syncRef.current = setInterval(() => refreshStatus().catch(() => {}), 30_000);
    return () => {
      if (syncRef.current) clearInterval(syncRef.current);
    };
  }, [refreshStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTicker();
      if (syncRef.current) clearInterval(syncRef.current);
    };
  }, [stopTicker]);

  // Handle Checkout return: /?purchase=success&cs_id=...
  useEffect(() => {
    const url = new URL(window.location.href);
    const purchase = url.searchParams.get("purchase");
    const csId = url.searchParams.get("cs_id");
    if (purchase === "success") {
      (async () => {
        try {
          if (csId) {
            // Ensure ledger(+1) even without webhook
            await apiPost(`${API}/billing/reconcile-checkout`, { session_id: csId });
          }
          await pollForCreditThenStart();
          await refreshStatus();
        } finally {
          url.searchParams.delete("purchase");
          url.searchParams.delete("cs_id");
          window.history.replaceState({}, "", url.toString());
        }
      })();
    } else if (purchase === "cancel") {
      url.searchParams.delete("purchase");
      window.history.replaceState({}, "", url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Actions ───────────────────────────────────────────────────
  const openPaymentModal = useCallback(async () => {
    try {
      if (status.has_saved_card) {
        try {
          await apiPost(`${API}/billing/pay-with-saved`);
          await pollForCreditThenStart();
          await refreshStatus();
          return;
        } catch (_e) {
          // fall through to Checkout
        }
      }
      const { url } = await apiPost(`${API}/billing/create-checkout-session`);
      window.location.assign(url);
    } catch (err) {
      console.error("Checkout create failed:", err);
      const msg = (err && err.message) || "Payment start failed";
      window.dispatchEvent(new CustomEvent("vybly-toast", { detail: { type: "error", msg } }));
      throw err;
    }
  }, [status.has_saved_card, refreshStatus]);

  const startSession = useCallback(async () => {
    const r = await apiPost(`${API}/billing/start-session`, {});
    await refreshStatus(); // re-anchor ticker to new expires_at
    return r;
  }, [refreshStatus]);

  async function pollForCreditThenStart() {
    for (let i = 0; i < STATUS_POLL_MAX_TRIES; i++) {
      const s = await apiGet(`${API}/billing/status`);
      if ((s.credits || 0) > 0) {
        await apiPost(`${API}/billing/start-session`, {});
        return;
      }
      await new Promise((r) => setTimeout(r, STATUS_POLL_MS));
    }
    throw new Error("Payment succeeded but credit not issued yet.");
  }

  const ctx = useMemo(
    () => ({
      loading,
      credits: status.credits,
      activeWindow: status.active_window || status.active_session || null,
      secondsRemaining, // <- state-driven; consumers re-render each second
      hasSavedCard: !!status.has_saved_card,
      openPaymentModal,
      startSession,
      refreshStatus,
    }),
    [
      loading,
      status.credits,
      status.active_window,
      status.active_session,
      status.has_saved_card,
      secondsRemaining,
      openPaymentModal,
      startSession,
      refreshStatus,
    ]
  );

  return (
    <Ctx.Provider value={ctx}>
      {children}

      {/* Optional in-app Payment Element (unused by default) */}
      {false && modalOpen && clientSecret && stripePromise && (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
          <PaymentModal
            onClose={() => setModalOpen(false)}
            onSuccess={async () => {
              await pollForCreditThenStart();
              await refreshStatus();
              setModalOpen(false);
            }}
            onError={(m) => console.error("Payment error:", m)}
          />
        </Elements>
      )}
    </Ctx.Provider>
  );
}

export function useBilling() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useBilling must be used inside BillingProvider");
  return ctx;
}

// ── Optional Payment Element modal (kept for future) ────────────
function PaymentModalInner({ onClose, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!stripe || !elements) return;
      setSubmitting(true);
      try {
        const { error } = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: window.location.origin + "/billing/return" },
          redirect: "if_required",
        });
        if (error) onError?.(error.message || "Payment failed");
        else onSuccess?.();
      } catch (err) {
        onError?.(err.message || "Payment failed");
      } finally {
        setSubmitting(false);
      }
    },
    [stripe, elements, onSuccess, onError]
  );

  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Start 1-Hour Session — $1.99</h2>
          <button className="text-gray-500 hover:text-black" onClick={onClose} aria-label="Close">×</button>
        </div>
        <form onSubmit={submit}>
          <PaymentElement options={{ layout: "tabs" }} />
          <button
            type="submit"
            disabled={submitting}
            className="mt-4 w-full rounded-lg border px-3 py-2 font-medium disabled:opacity-50"
          >
            {submitting ? "Processing…" : "Pay $1.99 and Start"}
          </button>
        </form>
        <p className="text-xs text-gray-500 mt-3">Your card is saved securely by Stripe for 1-click renewals.</p>
      </div>
    </div>
  );
}

function PaymentModal(props) {
  return <PaymentModalInner {...props} />;
}
